import { useTheme } from "@/app/use-theme";
import { nextPreference, PREFERENCE_LABEL, type ThemePreference } from "@/lib/theme";
import { MonitorIcon, MoonIcon, SunIcon } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

const ICON: Record<ThemePreference, typeof SunIcon> = {
  light: SunIcon,
  dark: MoonIcon,
  system: MonitorIcon,
};

/**
 * Light → Dark → System, in one button.
 *
 * A three-state cycle rather than a two-state switch, because `system` is a
 * real choice people want and a switch cannot express it. The icon shows the
 * *current* preference, not the one a click would produce — a control that
 * displays its own next state is the classic ambiguity in theme toggles, and
 * the `title` carries what the click will do instead.
 *
 * The label is announced through `aria-label`, so a screen reader gets "Theme:
 * System" rather than an unlabelled icon button.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { preference, setPreference } = useTheme();
  const Glyph = ICON[preference];
  const upcoming = nextPreference(preference);

  return (
    <button
      type="button"
      onClick={() => setPreference(upcoming)}
      aria-label={`Theme: ${PREFERENCE_LABEL[preference]}. Switch to ${PREFERENCE_LABEL[upcoming].toLowerCase()}.`}
      title={`Switch to ${PREFERENCE_LABEL[upcoming].toLowerCase()} theme`}
      className={cn(
        "text-content-muted hover:text-content hover:bg-surface-overlay",
        "border-border hover:border-border-strong inline-flex h-9 items-center gap-2 rounded-control border px-2.5",
        "text-xs font-medium transition-colors duration-150",
        className,
      )}
    >
      <Glyph className="size-4 shrink-0" />
      {/* The word is dropped on narrow viewports, where the icon alone is
          unambiguous enough and horizontal space in the bar is scarce. */}
      <span className="hidden sm:inline">{PREFERENCE_LABEL[preference]}</span>
    </button>
  );
}
